import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Loader from '@justgiving/loading/es/Loading';
import { captureException } from '@sentry/browser';
import { Form, Formik, FormikHelpers } from 'formik';

import {
  FitnessApplicationActivityType,
  FitnessApplicationSettingsInput,
  MeasurementSystem,
} from '../../../../types/__generated__';
import { GetStravaSettingsQuery } from '../../../../types/__generated__';
import { ConnectionStatus } from '../../../hooks/fitness/useFitnessConnection';
import useStravaSettings from '../../../hooks/fitness/useStravaSettings';
import { useApp } from '../../../hooks/useApp';
import useFullPageQuery from '../../../hooks/useFullPageQuery';
import usePageViewTracking from '../../../hooks/usePageViewTracking';
import Button from '../../Button/Button';
import { FormikChoiceGroup as ChoiceGroup } from '../ChoiceGroup';
import { Fieldset } from '../Fieldset';
import StravaLogoIcon from '../StravaIcon/StravaLogoIcon';
import { ModalType } from '../StravaModal';
import { FormikSwitch as Switch } from '../Switch';
import getStravaSettings from './StravaSettingsModal.gql';
import { defaultStravaSettings } from './StravaSettingsModalConsts';
import SettingsModal from '../../SettingsModal/SettingsModal';

export type FitnessSettingsFormValues = {
  __typename?: string;
  subscribedActivities: FitnessApplicationActivityType[] | [];
  measurementSystem: MeasurementSystem;
  showDistance: boolean;
  showDuration: boolean;
  showElevation: boolean;
  showPhotos: boolean;
  showMap: boolean;
};

export type Strava = {
  __typename?: string;
  settings: FitnessSettingsFormValues;
};

export const createStravaInput = (
  values: FitnessSettingsFormValues,
  id: string,
) => {
  return { pageId: id, name: 'Strava', ...values };
};

interface SettingsModalProps {
  connectionStatus?: ConnectionStatus;
  onClose: () => void;
  onModalTypeChange: (type: ModalType) => void;
}

export const StravaSettingsModal = React.forwardRef(
  (
    { onClose, onModalTypeChange, connectionStatus }: SettingsModalProps,
    ref?: React.Ref<HTMLDivElement>,
  ) => {
    const { t } = useTranslation('common', { useSuspense: false });
    const { data } = useFullPageQuery<GetStravaSettingsQuery>(
      getStravaSettings,
      {
        context: {
          credentials: 'include',
        },
      },
    );
    const [setFitnessApplicationSettings] = useStravaSettings();
    const { showToast } = useApp();

    const { title, id: pageId, strava } = data!.page!;

    const subscribedActivities = strava?.settings?.subscribedActivities;
    const isStravaPaused = subscribedActivities?.length === 0;
    const hasSettings = Boolean(strava);

    useEffect(() => {
      if (!hasSettings) {
        const input: FitnessApplicationSettingsInput = createStravaInput(
          defaultStravaSettings,
          pageId,
        );
        setFitnessApplicationSettings(input);
      }
    }, [hasSettings, pageId, setFitnessApplicationSettings]);

    const onSubmit = async (
      values: FitnessSettingsFormValues,
      { setSubmitting }: FormikHelpers<FitnessSettingsFormValues>,
    ) => {
      if (!values.subscribedActivities.length && subscribedActivities?.length) {
        onModalTypeChange('confirmation');
        return;
      }

      const input: FitnessApplicationSettingsInput = createStravaInput(
        values,
        pageId,
      );

      setSubmitting(true);
      try {
        await setFitnessApplicationSettings(input);

        const message = (() => {
          if (
            !hasSettings ||
            (connectionStatus === 'success' &&
              localStorage.getItem('strava.initialFormSave'))
          ) {
            return t('strava.connection.success');
          }
          return t('strava.update.success');
        })();

        showToast({
          message,
          type: 'success',
        });
      } catch (error) {
        showToast({
          message: t('strava.update.warning'),
          type: 'warning',
        });
        captureException(error);
      }
      localStorage.removeItem('strava.initialFormSave');
      setSubmitting(false);
      onClose();
    };

    const manageStravaSettings = (strava: Strava) => {
      if (strava && strava.settings) {
        const { __typename, ...userStravaSettings } = strava.settings;
        return userStravaSettings;
      }
      return defaultStravaSettings;
    };

    usePageViewTracking({
      event: 'page view',
      subtype: 'manage strava settings',
      page_section: 'strava',
    });

    return (
      <SettingsModal
        title={title}
        onCloseRequest={onClose}
        aria-label={t('strava.settings.label')}
      >
        <div ref={ref}>
          <div className="jg-display-flex jg-flex-aic jg-flex-column jg-flex-jcc jg-space-ptml jg-space-pbms">
            <StravaLogoIcon
              className="jg-space-mbms"
              isDisabled={isStravaPaused}
            />

            <div className="jg-h1 jg-space-mbmd">
              {t('strava.settings.title')}
            </div>
            <div className="jg-text--large-regular jg-space-mbml">
              {t('strava.settings.desc-part-1')}
            </div>
            <div className="jg-text--large-regular">
              {t('strava.settings.desc-part-2')}
            </div>
          </div>

          <Formik
            onSubmit={onSubmit}
            enableReinitialize={true}
            initialValues={manageStravaSettings(strava as Strava)}
          >
            {({ isValid, isSubmitting }) => (
              <Form>
                <div className="jg-display-flex jg-flex-ais jg-flex-column jg-flex-jcc jg-space-ptml jg-space-pbms">
                  <ChoiceGroup
                    label={t('strava.settings.activityTypes')}
                    error={
                      isStravaPaused
                        ? t('strava.settings.errorPaused')
                        : undefined
                    }
                    name="subscribedActivities"
                    multiple={true}
                    choices={[
                      {
                        label: t('strava.settings.run'),
                        value: FitnessApplicationActivityType.Run,
                      },
                      {
                        label: t('strava.settings.ride'),
                        value: FitnessApplicationActivityType.Ride,
                      },
                      {
                        label: t('strava.settings.walk'),
                        value: FitnessApplicationActivityType.Walk,
                      },
                      {
                        label: t('strava.settings.swim'),
                        value: FitnessApplicationActivityType.Swim,
                      },
                    ]}
                  />
                </div>
                <div className="jg-display-flex jg-flex-ais jg-flex-column jg-flex-jcc jg-space-ptsm jg-space-pbsm">
                  <ChoiceGroup
                    label={t('strava.settings.measurementSystem')}
                    name="measurementSystem"
                    choices={[
                      {
                        label: t('strava.settings.imperial'),
                        value: MeasurementSystem.Imperial,
                      },
                      {
                        label: t('strava.settings.metric'),
                        value: MeasurementSystem.Metric,
                      },
                    ]}
                  />
                </div>
                <div className="jg-display-flex jg-flex-ais jg-flex-column jg-flex-jcc jg-space-ptsm jg-space-pbsm">
                  <Fieldset legend={t('strava.settings.displayPreferences')}>
                    <Switch
                      name="showDistance"
                      label={t('strava.settings.showDistance')}
                      hint={t(
                        'strava.settings.showDistance-description',
                        'Display how far you’ve travelled',
                      )}
                    />
                    <Switch
                      name="showDuration"
                      label={t('strava.settings.showDuration')}
                      hint={t(
                        'strava.settings.showDuration-description',
                        'Show how long your activity took',
                      )}
                    />
                    <Switch
                      name="showElevation"
                      label={t('strava.settings.showElevation')}
                      hint={t(
                        'strava.settings.showElevation-description',
                        'Show elevation for your activity',
                      )}
                    />
                    <Switch
                      name="showPhotos"
                      label={t('strava.settings.showPhotos')}
                      hint={t(
                        'strava.settings.showPhotos-description',
                        'Include your Strava photos',
                      )}
                    />
                    <Switch
                      name="showMap"
                      label={t('strava.settings.showMap')}
                      hint={t(
                        'strava.settings.showActivityMap',
                        'Show your Strava activity map',
                      )}
                    />
                  </Fieldset>
                </div>

                <div>
                  <Button
                    disabled={isSubmitting || !isValid}
                    type="submit"
                    trackingData={{
                      subtype: 'button',
                      event: 'link click',
                      page_section: 'strava',
                    }}
                  >
                    {isSubmitting ? (
                      <Loader size="small" colour="white" />
                    ) : (
                      t('strava.settings.save')
                    )}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>

          {!isStravaPaused && (
            <Button
              className="jg-btn--ghost jg-space-mtms"
              onClick={() => onModalTypeChange('confirmation')}
              type="button"
              trackingData={{
                subtype: 'button',
                event: 'link click',
                page_section: 'strava',
              }}
            >
              {t('strava.settings.stopSharing')}
            </Button>
          )}
        </div>
      </SettingsModal>
    );
  },
);

StravaSettingsModal.displayName = 'StravaSettingsModal';
